import React, { useState, useEffect } from 'react';
import { fetchAuthSession } from '@aws-amplify/auth';

const Contact = () => {
  // Tab and UI states
  const [activeTab, setActiveTab] = useState('support');  
  const [isLoading, setIsLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState('');
  const [userEmail, setUserEmail] = useState('');

  // Review form states
  const [rating, setRating] = useState(5);
  const [hoveredStar, setHoveredStar] = useState(null);
  
  // Support ticket form data
  const [supportTicketForm, setSupportTicketForm] = useState({
    email: '',
    subject: '',
    message: ''
  });

  // Get user email on component start
  useEffect(() => {
    const getUserEmail = async () => {
      try {
        const session = await fetchAuthSession();
        const email = session.tokens.idToken.payload.email;
        setUserEmail(email);
        setSupportTicketForm(prev => ({ ...prev, email }));
      } catch (error) {
        console.error('Error getting user email:', error);
      }
    };
    getUserEmail();
  }, []);

  const handleSupportTicketInputChange = (e) => {
    const { name, value } = e.target;
    setSupportTicketForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSupportTicketSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setSubmitError('');
    setSubmitSuccess('');

    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens.idToken.toString();
      const email = session.tokens.idToken.payload.email;

      const requestBody = {
        email: email,
        subject: supportTicketForm.subject,
        message: supportTicketForm.message,
        category: supportTicketForm.subject || 'general'
      };
      
      console.log('Session email:', email);
      console.log('Request body:', requestBody);
      console.log('Request body stringified:', JSON.stringify(requestBody));

      const response = await fetch(`${process.env.REACT_APP_API_GATEWAY_URL}/submit-support-ticket`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
          'access-control-allow-headers': 'Content-Type,X-Amz-Date,Authorization,X-Api-Key,X-Amz-Security-Token,access-control-allow-headers,access-control-allow-origin,access-control-allow-methods',
          'access-control-allow-origin': '*',
          'access-control-allow-methods': 'OPTIONS,POST'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error('Failed to submit support ticket');
      }

      setSubmitSuccess('Your support ticket has been submitted successfully!');
      setSupportTicketForm(prev => ({ ...prev, subject: '', message: '' })); // Keep email
    } catch (error) {
      setSubmitError('Failed to submit support ticket. Please try again.');
      console.error('Submit error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-12">
      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-8">
        <button
          onClick={() => setActiveTab('support')}
          className={`py-4 px-6 text-sm font-medium border-b-2 ${
            activeTab === 'support'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          Write to Us
        </button>
        {/* <button
          onClick={() => setActiveTab('review')}
          className={`py-4 px-6 text-sm font-medium border-b-2 ${
            activeTab === 'review'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          Tell Us About Your Experience
        </button> */}
      </div>

      {/* Success/Error Messages */}
      {submitSuccess && (
        <div className="mb-4 p-4 bg-green-50 text-green-700 rounded-lg">
          Your message has been sent successfully!
        </div>
      )}
      {submitError && (
        <div className="mb-4 p-4 bg-red-50 text-red-700 rounded-lg">
          Failed to send message. Please try again.
        </div>
      )}

      {/* Support Ticket Form */}
      {activeTab === 'support' && (
        <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100">
          <h3 className="text-xl font-medium mb-6">Need Help?</h3>
          <form className="space-y-6" onSubmit={handleSupportTicketSubmit}>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Your Email
              </label>
              <input
                type="email"
                name="email"
                value={supportTicketForm.email}
                readOnly
                className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-gray-50 focus:outline-none"
                placeholder="Your email address"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subject
              </label>
              <select 
                name="subject"
                value={supportTicketForm.subject}
                onChange={handleSupportTicketInputChange}
                className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select a subject</option>
                <option value="technical">Technical Issue</option>
                <option value="billing">Billing Issue</option>
                <option value="feedback">General Feedback</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Message
              </label>
              <textarea
                name="message"
                value={supportTicketForm.message}
                onChange={handleSupportTicketInputChange}
                className="w-full h-32 px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="How can we help you?"
                required
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className={`w-full bg-blue-500 text-white py-3 px-6 rounded-lg hover:bg-blue-600 transition-colors ${
                isLoading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isLoading ? 'Sending...' : 'Send Message'}
            </button>
          </form>
        </div>
      )}

      {/* Review Form - Commented out */}
      {/* {activeTab === 'review' && (
        <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100">
          <h3 className="text-xl font-medium mb-6">Share Your Experience</h3>
          <form className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Your Name
              </label>
              <input
                type="text"
                className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter your full name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Your Email
              </label>
              <input
                type="email"
                value={userEmail}
                readOnly
                className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-gray-50 focus:outline-none"
                placeholder="Your email address"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Your Role
              </label>
              <input
                type="text"
                className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Software Engineer, Product Manager"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rating
              </label>
              <div className="flex space-x-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRating(star)}
                    onMouseEnter={() => setHoveredStar(star)}
                    onMouseLeave={() => setHoveredStar(null)}
                    className="text-yellow-400 hover:text-yellow-500"
                  >
                    <svg 
                      className="h-8 w-8" 
                      fill={star <= (hoveredStar || rating) ? "currentColor" : "none"}
                      stroke="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Your Review
              </label>
              <textarea
                className="w-full h-32 px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Share your experience with our service..."
              />
            </div>

            <button
              type="submit"
              className="w-full bg-blue-500 text-white py-3 px-6 rounded-lg hover:bg-blue-600 transition-colors"
            >
              Submit Review
            </button>
          </form>
        </div>
      )} */}
    </div>
  );
};

export default Contact;